import type { DocumentTemplatePartData, LayoutCapacity } from "@virtual-brain-lib/types";

interface TextBlock {
	x: number;
	y: number;
	width: number;
	height: number;
	chars: number;
	preset?: string;
}

const PLAIN_PRESETS = new Set(["rect", "roundRect", "ellipse", "line", "straightConnector1"]);
const CLOUD_MIN_MEMBERS = 4;

interface SlotGroup {
	axis: "x" | "y";
	anchors: number[];
	chars: number;
}

const SIZE_TOLERANCE = 0.15;
const SKIPPED_PLACEHOLDERS = ["TITLE", "SUBTITLE", "FOOTER", "SLIDE_NUMBER", "DATE"];
const MAX_REPEATS = 3;
// Slots shorter than this are labels (a step number, a tag), not content holders.
const MIN_REPEAT_CHARS = 20;

export function computeLayoutCapacity(data: DocumentTemplatePartData): LayoutCapacity {
	const blocks: TextBlock[] = [];
	const tables: LayoutCapacity["tables"] = [];
	let tableChars = 0;

	for (const shape of data.shapes) {
		const table = asTable(shape);

		if (table) {
			tables.push({ rows: table.rows, cols: table.cols });
			tableChars += table.chars;
			continue;
		}

		const block = asTextBlock(shape);

		if (block) {
			blocks.push(block);
		}
	}

	return {
		textBlocks: blocks.length,
		chars: blocks.reduce((sum, block) => sum + block.chars, 0) + tableChars,
		repeats: detectRepeats(blocks),
		tables,
		visuals: data.visuals,
	};
}

function asTable(shape: Record<string, unknown>) {
	const content = shape.content as { table?: { cells?: unknown[][] } } | undefined;
	const cells = content?.table?.cells;

	if (!cells || cells.length === 0) {
		return undefined;
	}

	const chars = cells
		.flat()
		.reduce<number>((sum, cell) => sum + readChars((cell ?? {}) as Record<string, unknown>), 0);

	return { rows: cells.length, cols: cells[0].length, chars };
}

function asTextBlock(shape: Record<string, unknown>): TextBlock | undefined {
	const placeholder = (shape.placeholder as { type?: string } | undefined)?.type ?? "";
	const bbox = shape.bbox as number[] | undefined;

	if (SKIPPED_PLACEHOLDERS.some((name) => placeholder.includes(name))) {
		return undefined;
	}

	if (bbox?.length !== 4) {
		return undefined;
	}

	return {
		x: bbox[0],
		y: bbox[1],
		width: bbox[2],
		height: bbox[3],
		chars: readChars(shape),
		preset: typeof shape.preset === "string" ? shape.preset : undefined,
	};
}

function readChars(shape: Record<string, unknown>): number {
	// eslint-disable-next-line @typescript-eslint/naming-convention
	const budget = shape.text_budget as { max_chars_total?: number } | undefined;

	return budget?.max_chars_total ?? 0;
}

// Same-size blocks aligned on a row or a column are the slots of one repeated item; slot
// groups sharing the same anchors (a header row above a body row) belong to the same item.
function detectRepeats(blocks: TextBlock[]): LayoutCapacity["repeats"] {
	const groups: SlotGroup[] = [];
	const clouds = cloudRepeats(blocks);
	const inCloud = new Set(clouds.flatMap((cloud) => cloud.members));

	for (const members of groupBySize(blocks.filter((block) => !inCloud.has(block)))) {
		const group = alignedGroup(members);

		if (group) {
			groups.push(group);
		}
	}

	const items: SlotGroup[] = [];

	for (const group of groups) {
		const item = items.find((candidate) => sameAnchors(candidate, group));

		if (item) {
			item.chars += group.chars;
		} else {
			items.push({ ...group, anchors: [...group.anchors] });
		}
	}

	return [
		...clouds.map((cloud) => ({ count: cloud.members.length, charsEach: cloud.chars })),
		...items.map((item) => ({ count: item.anchors.length, charsEach: item.chars })),
	]
		.filter((item) => item.charsEach >= MIN_REPEAT_CHARS)
		.sort((a, b) => b.count * b.charsEach - a.count * a.charsEach)
		.slice(0, MAX_REPEATS);
}

function cloudRepeats(blocks: TextBlock[]): Array<{ members: TextBlock[]; chars: number }> {
	const byPreset = new Map<string, TextBlock[]>();

	for (const block of blocks) {
		if (block.preset && !PLAIN_PRESETS.has(block.preset)) {
			byPreset.set(block.preset, [...(byPreset.get(block.preset) ?? []), block]);
		}
	}

	return [...byPreset.values()]
		.filter((members) => members.length >= CLOUD_MIN_MEMBERS)
		.map((members) => ({
			members,
			chars: Math.round(
				members.reduce((sum, block) => sum + block.chars, 0) / members.length,
			),
		}));
}

function groupBySize(blocks: TextBlock[]): TextBlock[][] {
	const groups: TextBlock[][] = [];

	for (const block of blocks) {
		const group = groups.find((candidate) => sameSize(candidate[0], block));

		if (group) {
			group.push(block);
		} else {
			groups.push([block]);
		}
	}

	return groups.filter((group) => group.length >= 2);
}

function sameSize(a: TextBlock, b: TextBlock): boolean {
	return (
		Math.abs(a.width - b.width) <= SIZE_TOLERANCE * Math.max(a.width, b.width) &&
		Math.abs(a.height - b.height) <= SIZE_TOLERANCE * Math.max(a.height, b.height)
	);
}

function alignedGroup(members: TextBlock[]): SlotGroup | undefined {
	const xs = distinct(
		members.map((block) => block.x),
		members[0].width * SIZE_TOLERANCE,
	);
	const ys = distinct(
		members.map((block) => block.y),
		members[0].height * SIZE_TOLERANCE,
	);
	const chars = Math.round(members.reduce((sum, block) => sum + block.chars, 0) / members.length);

	if (ys.length === 1 && xs.length === members.length) {
		return { axis: "x", anchors: xs, chars };
	}

	if (xs.length === 1 && ys.length === members.length) {
		return { axis: "y", anchors: ys, chars };
	}

	if (xs.length >= 2 && ys.length >= 2 && xs.length * ys.length === members.length) {
		return { axis: "x", anchors: members.map((block) => block.x * 7 + block.y), chars };
	}

	return undefined;
}

function distinct(values: number[], tolerance: number): number[] {
	const sorted = [...values].sort((a, b) => a - b);

	return sorted.filter((value, index) => index === 0 || value - sorted[index - 1] > tolerance);
}

function sameAnchors(a: SlotGroup, b: SlotGroup): boolean {
	if (a.axis !== b.axis || a.anchors.length !== b.anchors.length) {
		return false;
	}

	const span = Math.max(...a.anchors) - Math.min(...a.anchors) || 1;
	const tolerance = span / a.anchors.length / 3;

	return a.anchors.every((anchor, index) => Math.abs(anchor - b.anchors[index]) <= tolerance);
}
